export type NumberFormatType =
  | "normal"
  | "percentage"
  | "usd"
  | "eur"
  | "jpy"
  | "btc";

const CURRENCY_SYMBOLS: Record<string, string> = {
  usd: "$",
  eur: "€",
  jpy: "¥",
  btc: "₿",
  percentage: "%",
};

/**
 * Returns the currency/format symbol for a given numberFormat key.
 * Returns null for "normal" or unrecognized formats.
 */
export function getNumberFormatSymbol(
  format: string | undefined | null,
): string | null {
  if (!format || format === "normal") return null;
  return CURRENCY_SYMBOLS[format] ?? null;
}

/**
 * Formats a numeric value with its currency/format symbol for display.
 * The raw number is formatted with locale separators; the symbol is prepended
 * (or appended for percentage).
 */
export function formatNumberWithSymbol(
  val: unknown,
  format?: string | null,
): string {
  if (val == null || val === "") return "-";
  const num = Number(val);
  if (Number.isNaN(num)) {
    if (typeof val === "string") return val;
    return "";
  }

  const formatted = num.toLocaleString("en-US", { maximumFractionDigits: 10 });
  const symbol = getNumberFormatSymbol(format);

  if (!symbol) return formatted;
  if (format === "percentage") return `${formatted}${symbol}`;
  return `${symbol}${formatted}`;
}

function clampToMax(value: string, max: number | null | undefined): string {
  if (typeof max === "number" && max > 0 && value.length > max) {
    return value.slice(0, max);
  }
  return value;
}

function applyDecimalPrecision(
  dec: string,
  precision: number | null | undefined,
): string {
  if (typeof precision === "number" && precision >= 0) {
    return dec.slice(0, precision);
  }
  return dec;
}

function splitOnFirstDot(val: string): string[] {
  const parts = val.split(".");
  if (parts.length <= 2) return parts;
  return [parts[0], parts.slice(1).join("")];
}

function formatMaskedRTL(
  val: string,
  allowNegative: boolean,
  precision: number,
  maxIntegers: number | null | undefined,
): string {
  const hadMinus = allowNegative && val.startsWith("-");
  let digits = (val.match(/\d+/g) || []).join("");

  if (!digits) return hadMinus ? "-" : "";

  if (typeof maxIntegers === "number" && maxIntegers > 0) {
    const maxDigits = maxIntegers + precision;
    if (digits.length > maxDigits) digits = digits.slice(0, maxDigits);
  }

  let intPart: string;
  let fracPart: string;
  if (digits.length > precision) {
    intPart = digits.slice(0, -precision).replace(/^0+(?=\d)/, "");
    fracPart = digits.slice(-precision);
  } else {
    intPart = "0";
    fracPart = digits.padStart(precision, "0");
  }

  return hadMinus ? `-${intPart}.${fracPart}` : `${intPart}.${fracPart}`;
}

function formatLTRDecimal(
  val: string,
  allowNegative: boolean,
  precision: number | null | undefined,
  maxIntegers: number | null | undefined,
): string {
  const cleaned = allowNegative
    ? val.replace(/[^0-9.-]/g, "").replace(/(?!^)-/g, "")
    : val.replace(/[^0-9.]/g, "");

  const parts = splitOnFirstDot(cleaned);
  const isNegative = allowNegative && parts[0].startsWith("-");
  const absInt = isNegative ? parts[0].slice(1) : parts[0];
  const integerPart = isNegative
    ? `-${clampToMax(absInt, maxIntegers)}`
    : clampToMax(absInt, maxIntegers);

  if (precision === 0) return integerPart;

  if (parts.length > 1) {
    return `${integerPart}.${applyDecimalPrecision(parts[1], precision)}`;
  }

  return integerPart;
}

function formatInteger(
  val: string,
  allowNegative: boolean,
  maxIntegers: number | null | undefined,
): string {
  const cleaned = allowNegative
    ? val.replace(/[^0-9-]/g, "").replace(/(?!^)-/g, "")
    : val.replace(/\D/g, "");

  const isNegative = allowNegative && cleaned.startsWith("-");
  const absVal = isNegative ? cleaned.slice(1) : cleaned;

  return isNegative
    ? `-${clampToMax(absVal, maxIntegers)}`
    : clampToMax(absVal, maxIntegers);
}

/**
 * Sanitizes free text into a valid decimal amount string for a money input.
 * Keeps only digits and a single decimal separator (extra dots are dropped)
 * and strips leading zeros, so "0001212" renders as "1212" while "0.5" and a
 * lone "0" are preserved. Returns "" when there is no numeric content.
 *
 * A trailing dot is preserved so the user can keep typing decimals mid-edit;
 * use `stripTrailingDot` (e.g. on blur) to normalize the final value.
 *
 * @param maxDecimals when provided, limits the decimal part to that many digits.
 */
export function sanitizeDecimalAmount(
  raw: string,
  maxDecimals?: number,
): string {
  const cleaned = (raw ?? "").replace(/[^\d.]/g, "");
  const [intPart = "", decPart] = cleaned.split(".");
  const normalizedInt = intPart.replace(/^0+(?=\d)/, "");
  if (decPart === undefined) return normalizedInt;
  const limitedDec =
    typeof maxDecimals === "number" ? decPart.slice(0, maxDecimals) : decPart;
  return `${normalizedInt}.${limitedDec}`;
}

/**
 * Removes a trailing decimal separator left over from mid-edit input, so a
 * value like "1232." becomes "1232". Any other value is returned unchanged.
 */
export function stripTrailingDot(value: string): string {
  return value.endsWith(".") ? value.slice(0, -1) : value;
}

export function formatNumberInput(
  value: string,
  allowDecimals: boolean,
  allowNegative: boolean,
  precision?: number | null,
  maxIntegers?: number | null,
): string {
  const val = value ?? "";

  const isMaskedRTL =
    allowDecimals &&
    typeof precision === "number" &&
    precision >= 1 &&
    precision <= 4;

  if (isMaskedRTL) {
    return formatMaskedRTL(val, allowNegative, precision, maxIntegers);
  }

  if (allowDecimals) {
    return formatLTRDecimal(val, allowNegative, precision, maxIntegers);
  }

  return formatInteger(val, allowNegative, maxIntegers);
}
